import * as React from "react";
import { useState } from "react";
import { Spinner } from "@finster/ui";
import { ExtractedImage } from "../services/pdfService";
import { insertImageToCurrentSlide } from "../services/slideService";

interface ImageInsertPanelProps {
  images: ExtractedImage[];
  onLog?: (message: string) => void;
}

export const ImageInsertPanel: React.FC<ImageInsertPanelProps> = ({ images, onLog }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isInserting, setIsInserting] = useState(false);
  const [insertingIndex, setInsertingIndex] = useState<number | null>(null);

  const log = (msg: string) => {
    console.log(msg);
    onLog?.(msg);
  };

  const handleInsertImage = async (image: ExtractedImage, index: number) => {
    setIsInserting(true);
    setInsertingIndex(index);

    try {
      log(`Inserting image ${index + 1} to current slide...`);
      await insertImageToCurrentSlide(image, log);
      log(`✓ Image inserted to slide`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      log(`❌ Failed to insert: ${errorMsg}`);
    } finally {
      setIsInserting(false);
      setInsertingIndex(null);
    }
  };

  if (images.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        margin: "12px 0",
        border: "1px solid hsl(var(--primary-muted))",
        borderRadius: "10px",
        overflow: "hidden",
          backgroundColor: "hsl(var(--card))",
      }}
    >
      {/* Compact header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: "100%",
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "hsl(var(--primary) / 0.06)",
          border: "none",
          cursor: "pointer",
          fontSize: "12px",
          fontWeight: 600,
          color: "hsl(var(--primary))",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ 
            width: "20px", 
            height: "20px", 
            borderRadius: "5px",
            backgroundColor: "hsl(var(--primary))",
            color: "hsl(var(--primary-foreground))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "11px",
            fontWeight: 700,
          }}>
            {images.length}
          </span>
          <span>PDF images ready to insert</span>
        </div>
        <span style={{ 
          fontSize: "10px",
          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
          opacity: 0.7,
        }}>
          ▼
        </span>
      </button>

      {/* Expanded image grid */}
      {isExpanded && (
        <div
          style={{
            padding: "12px",
            maxHeight: "180px",
            overflowY: "auto",
          }}
        >
          <p style={{ 
            margin: "0 0 10px", 
            fontSize: "11px", 
            color: "hsl(var(--muted-foreground))",
          }}>
            Click to insert on current slide
          </p>
          
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(60px, 1fr))",
              gap: "6px",
            }}
          >
            {images.map((img, index) => (
              <button
                key={index}
                onClick={() => handleInsertImage(img, index)}
                disabled={isInserting}
                style={{
                  position: "relative",
                  padding: 0,
                  border: "none",
                  borderRadius: "6px",
                  overflow: "hidden",
                  cursor: isInserting ? "wait" : "pointer",
                  backgroundColor: "hsl(var(--accent))",
                  opacity: isInserting && insertingIndex !== index ? 0.4 : 1,
                  transition: "transform 0.1s, opacity 0.15s",
                  transform: "scale(1)",
                }}
                title={`Page ${img.page_number} - ${img.width}×${img.height}`}
                onMouseEnter={(e) => { if (!isInserting) e.currentTarget.style.transform = "scale(1.05)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
              >
                <img
                  src={`data:image/${img.format};base64,${img.base64_data}`}
                  alt={`Image ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "44px",
                    objectFit: "cover",
                    borderRadius: "6px",
                  }}
                />
                
                {/* Loading overlay */}
                {isInserting && insertingIndex === index && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "hsl(var(--primary) / 0.85)",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Spinner size="sm" />
                  </div>
                )}
                
                {/* Page number badge */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "3px",
                    right: "3px",
                    padding: "1px 5px",
                    backgroundColor: "rgba(0,0,0,0.6)",
                    borderRadius: "4px",
                    fontSize: "9px",
                    fontWeight: 600,
                    color: "#fff",
                  }}
                >
                  {img.page_number}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageInsertPanel;
